/* ============================================================
   scroll-image-progress — 滚动驱动序列帧
   滚动 / sticky / cover 逻辑对齐 sections/scroll-video.liquid
   ============================================================ */

class ScrollImageProgress extends HTMLElement {
  constructor() {
    super();

    this._sectionId = this.getAttribute('section-id') || '';
    this._imageCount = parseInt(this.getAttribute('image-count'), 10) || 100;
    this._imageStartUrl = this.getAttribute('image-start-url') || '';
    this._imageStartId = this.getAttribute('image-start-id') || '00001';
    this._wrapperHeight = parseInt(this.getAttribute('wrapper-height'), 10) || 300;
    this._scrollEndOffset = parseInt(this.getAttribute('scroll-end-offset'), 10) || 100;
    this._enableCoverScroll = this.getAttribute('enable-cover-scroll') === 'true';
    this._hasHeadline = this.getAttribute('has-headline') === 'true';
    this._hasTip = this.getAttribute('has-tip') === 'true';
    this._hasPoster = this.getAttribute('has-poster') === 'true';
    this._canvasWidth = parseInt(this.getAttribute('canvas-width'), 10) || 1920;
    this._canvasHeight = parseInt(this.getAttribute('canvas-height'), 10) || 1080;

    this._posterHidden = false;
    this._images = [];
    this._curFrame = -1;
    this._coverActive = false;
    this._coverStyle = null;
    this._isCoverFixed = false;
    this._coverPlaceholder = null;
    this._nextSection = null;
    this._onScrollBound = this._onScroll.bind(this);
    this._onResizeBound = this._onResize.bind(this);
    this._scrollRaf = null;
  }

  connectedCallback() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this._init(), { once: true });
    } else {
      this._init();
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this._onScrollBound);
    window.removeEventListener('resize', this._onResizeBound);
    if (this._scrollRaf) {
      cancelAnimationFrame(this._scrollRaf);
      this._scrollRaf = null;
    }
    if (this._coverStyle) {
      this._coverStyle.remove();
      this._coverStyle = null;
    }
    this._removeCoverPlaceholder();
  }

  _init() {
    this._imagesWrapper = this.querySelector('.image-sequence-wrapper');
    this._stickyWrap = this.querySelector('.scene-container');
    this._canvas = this.querySelector('canvas');
    this._progressItems = this.querySelectorAll('.progress-content-item');
    this._progressWrapper = this.querySelector('.progress-content-wrapper');
    this._poster = this.querySelector('.poster');
    this._headline = this.querySelector('.scroll-title-main');
    this._tipWrapper = this.querySelector('.tip-content-wrapper');

    if (!this._imagesWrapper || !this._stickyWrap || !this._canvas) return;

    this._imagesWrapper.style.minHeight = this._wrapperHeight + 'vh';

    if (this._canvas.width) this._canvasWidth = this._canvas.width;
    if (this._canvas.height) this._canvasHeight = this._canvas.height;

    this._ctx = this._canvas.getContext('2d');
    this._animScrollRatio = Math.max(
      0.01,
      (this._wrapperHeight - this._scrollEndOffset) / this._wrapperHeight
    );

    this._buildFrameUrls();
    if (!this._urls.length) return;

    this._preloadImages();
    if (this._enableCoverScroll) this._setupCoverScroll();
    if (this._hasHeadline && this._headline) this._initHeadlineAnimation();

    window.addEventListener('scroll', this._onScrollBound, { passive: true });
    window.addEventListener('resize', this._onResizeBound);

    this._onScroll();
    setTimeout(() => this._onScroll(), 60);
  }

  /* ---------- 帧 URL ---------- */

  _buildFrameUrls() {
    if (!this._imageStartUrl || !this._imageStartId) {
      console.error('[scroll-image-progress] image_start_url or image_start_id missing');
      this._urls = [];
      return;
    }
    const parts = this._imageStartUrl.split(this._imageStartId);
    if (parts.length !== 2) {
      console.error('[scroll-image-progress] cannot split URL by image_start_id');
      this._urls = [];
      return;
    }
    const head = parts[0];
    const tail = parts[1];
    this._urls = Array.from({ length: this._imageCount }, (_, i) =>
      head + this._padIncrement(this._imageStartId, i) + tail
    );
  }

  _padIncrement(str, offset) {
    const num = parseInt(str, 10);
    if (isNaN(num)) return str;
    return (num + offset).toString().padStart(str.length, '0');
  }

  _preloadImages() {
    this._images = this._urls.map((url) => {
      const img = new Image();
      img.src = url;
      return img;
    });
    this._images[0].onload = () => this._drawFrame(0);
  }

  /* ---------- 滚动进度（与 scroll-video 的 computeScrollProgress 一致）---------- */

  _computeWrapperScrollProgress() {
    const rect = this._imagesWrapper.getBoundingClientRect();
    const viewportH = window.innerHeight;
    const start = rect.top + window.scrollY;
    const totalScrollable = Math.max(1, this._imagesWrapper.offsetHeight - viewportH);
    const scrolled = window.scrollY - start;
    return Math.min(1, Math.max(0, scrolled / totalScrollable));
  }

  /** 序列帧 0–1：在前 (wrapperHeight - scrollEndOffset) 比例内播完，之后保持最后一帧 */
  _computeAnimationProgress() {
    const full = this._computeWrapperScrollProgress();
    return Math.min(1, full / this._animScrollRatio);
  }

  _drawFrame(frameIndex) {
    const frame = Math.min(this._images.length - 1, Math.max(0, frameIndex));
    if (frame === this._curFrame) return;

    const img = this._images[frame];
    if (!img || !img.complete || img.naturalWidth <= 0) return;

    this._ctx.clearRect(0, 0, this._canvasWidth, this._canvasHeight);
    this._ctx.drawImage(img, 0, 0, this._canvasWidth, this._canvasHeight);
    this._curFrame = frame;

    if (this._hasPoster && this._poster && !this._posterHidden) {
      this._posterHidden = true;
      this._poster.style.opacity = '0';
      setTimeout(() => {
        if (this._poster && this._poster.parentNode) {
          this._poster.remove();
        }
      }, 260);
    }
  }

  _updateSequence() {
    const animProgress = this._computeAnimationProgress();
    const frame = Math.round(animProgress * (this._images.length - 1));
    this._drawFrame(frame);
    this._updateProgressOverlay(animProgress * 100);
  }

  _updateProgressOverlay(progress) {
    if (!this._progressWrapper) return;

    if (progress > 0 && progress < 100) {
      this._progressWrapper.classList.add('active');
    } else {
      this._progressWrapper.classList.remove('active');
    }

    this._progressItems.forEach((item) => {
      const start = parseFloat(item.dataset.progressStart) || 0;
      const end = parseFloat(item.dataset.progressEnd) || 100;
      const fadeIn = item.dataset.fadeIn === 'true';
      const type = item.dataset.progressType || 'none';
      const maxPercent = parseFloat(item.dataset.progressMaxPercent) || 100;

      let opacity = 0;
      if (progress >= start && progress <= end) {
        const fadeEnd = start + (end - start) * 0.2;
        opacity = fadeIn ? this._mapClamp(progress, start, fadeEnd) : 1;
      }

      item.classList.toggle('active', opacity > 0);

      const bar = item.querySelector('.progress-bar');
      if (bar && type !== 'none') {
        this._updateProgressBar(bar, type, opacity, maxPercent);
      }
    });

    if (this._hasTip && this._tipWrapper) {
      const lastItem = this._progressItems[this._progressItems.length - 1];
      if (lastItem && lastItem.classList.contains('active')) {
        this._tipWrapper.classList.add('show');
      } else {
        this._tipWrapper.classList.remove('show');
      }
    }
  }

  _updateProgressBar(bar, type, opacity, maxPercent) {
    if (opacity <= 0) {
      bar.style.opacity = '0';
      bar.style.width = '0px';
      bar.style.height = '0px';
      bar.className = 'progress-bar ' + type;
      return;
    }

    const containerRect = this._stickyWrap.getBoundingClientRect();
    const maxPixel =
      type === 'vertical'
        ? containerRect.height * (maxPercent / 100)
        : containerRect.width * (maxPercent / 100);

    bar.style.opacity = opacity;
    bar.className = 'progress-bar ' + type;

    if (type === 'vertical') {
      bar.style.width = '1px';
      bar.style.height = Math.round(maxPixel * opacity) + 'px';
    } else {
      bar.style.height = '1px';
      bar.style.width = Math.round(maxPixel * opacity) + 'px';
    }
  }

  _mapClamp(x, a, b) {
    if (b - a <= 0) return 0;
    return Math.min(1, Math.max(0, (x - a) / (b - a)));
  }

  /* ---------- Cover scroll（与 scroll-video.liquid 完全一致）---------- */

  _setupCoverScroll() {
    const sectionWrapper =
      document.getElementById('shopify-section-' + this._sectionId) ||
      this.closest('.shopify-section');

    if (!sectionWrapper || !sectionWrapper.id) return;

    const nextSection = sectionWrapper.nextElementSibling;
    const hasNextSection =
      nextSection &&
      (nextSection.classList.contains('shopify-section') ||
        nextSection.matches('[class*="shopify-section"]'));

    if (!hasNextSection) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this._nextSection = nextSection;
    this._coverActive = true;

    this._coverStyle = document.createElement('style');
    this._coverStyle.id = 'cover-scroll-css-' + this._sectionId;
    this._coverStyle.textContent =
      '#' + sectionWrapper.id + ' ~ .shopify-section{position:relative;z-index:10;}';
    document.head.appendChild(this._coverStyle);
  }

  _removeCoverPlaceholder() {
    if (this._coverPlaceholder) {
      this._coverPlaceholder.remove();
      this._coverPlaceholder = null;
    }
  }

  /** sticky → fixed 时保留占位，避免布局塌陷造成滚动跳动 */
  _insertCoverPlaceholder(heightPx) {
    this._removeCoverPlaceholder();
    const el = document.createElement('div');
    el.className = 'scene-container-cover-placeholder';
    el.setAttribute('aria-hidden', 'true');
    el.style.height = heightPx + 'px';
    el.style.width = '100%';
    el.style.pointerEvents = 'none';
    this._imagesWrapper.insertBefore(el, this._stickyWrap);
    this._coverPlaceholder = el;
  }

  _enterCoverFixed() {
    const rect = this._stickyWrap.getBoundingClientRect();
    this._insertCoverPlaceholder(rect.height);
    this._isCoverFixed = true;

    // 先用当前屏幕坐标贴合，避免 sticky 释放与 fixed 之间的 1 帧错位
    this._stickyWrap.style.position = 'fixed';
    this._stickyWrap.style.top = rect.top + 'px';
    this._stickyWrap.style.left = rect.left + 'px';
    this._stickyWrap.style.width = rect.width + 'px';
    this._stickyWrap.style.height = rect.height + 'px';
    this._stickyWrap.style.zIndex = '0';

    if (rect.top <= 1 && rect.left <= 1) {
      this._stickyWrap.style.top = '0';
      this._stickyWrap.style.left = '0';
      this._stickyWrap.style.width = '100%';
      this._stickyWrap.style.height = '100vh';
    }
  }

  _leaveCoverFixed() {
    this._isCoverFixed = false;
    this._removeCoverPlaceholder();
    this._stickyWrap.style.position = 'sticky';
    this._stickyWrap.style.top = '0';
    this._stickyWrap.style.left = '';
    this._stickyWrap.style.width = '';
    this._stickyWrap.style.height = '';
    this._stickyWrap.style.zIndex = '';
  }

  _updateCoverPhase() {
    if (!this._coverActive || !this._nextSection || !this._stickyWrap) return;

    const wrapperRect = this._imagesWrapper.getBoundingClientRect();
    const viewportH = window.innerHeight;
    const nextRect = this._nextSection.getBoundingClientRect();

    const stickyLost = wrapperRect.bottom <= viewportH;
    const nextCoversAll = nextRect.top <= 0 && nextRect.bottom >= viewportH;
    const wrapperBackInView = wrapperRect.top >= 0 && wrapperRect.bottom > viewportH;
    const shouldFix = stickyLost && !nextCoversAll && !wrapperBackInView;

    if (shouldFix && !this._isCoverFixed) {
      this._enterCoverFixed();
    } else if (!shouldFix && this._isCoverFixed) {
      this._leaveCoverFixed();
    }
  }

  /* ---------- 滚动 / 缩放标题 ---------- */

  _onScroll() {
    if (this._scrollRaf) return;
    this._scrollRaf = requestAnimationFrame(() => {
      this._scrollRaf = null;
      this._updateSequence();
      this._updateCoverPhase();
    });
  }

  _onResize() {
    this._onScroll();
  }

  _initHeadlineAnimation() {
    const scaleDelay = 0.3;
    const maxScale = 4;
    const fadeStart = 0.5;
    const root = document.getElementById('scroll-image-progress-' + this._sectionId) || this;

    const onScroll = () => {
      const rect = root.getBoundingClientRect();
      const vh = window.innerHeight;
      const progress = Math.min(Math.max(0, (vh - rect.top) / (vh * 1.2)), 1);

      let scale = 1;
      if (progress >= scaleDelay) {
        scale = 1 + ((progress - scaleDelay) / (1 - scaleDelay)) * (maxScale - 1);
      }
      this._headline.style.transform = 'scale(' + scale + ')';

      let opacity = 1;
      if (progress >= fadeStart) {
        opacity = 1 - (progress - fadeStart) / (1 - fadeStart);
      }
      this._headline.style.opacity = opacity;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    this._headlineScrollCleanup = () => window.removeEventListener('scroll', onScroll);
  }
}

if (!window.customElements.get('scroll-image-progress')) {
  window.customElements.define('scroll-image-progress', ScrollImageProgress);
}
